import pipefitterApi from '../../../services/pipefitterApi';
import { normalizePipeIdsForGroup } from '../helpers/reportPipeIds';
import {
  parseReportFileRefs,
  pickReportFileNameByType,
  pickReportFileUrlByType,
  type ReportFileRef,
} from '../helpers/normalizeReportFileUrl';
import {
  getReportVersions,
  isReportVersionsFailure,
  normalizeReportVersionRow,
  type ReportVersionRow,
} from './reportVersions';
import { PIPEFITTER_REPORT_TYPES } from '../constants/reportVersionTypes';

export interface ReportHistoryPipeRow {
  pipeEndID: string;
  minID?: number | null;
  avgID?: number | null;
  maxID?: number | null;
  ooR?: number | null;
  minOD?: number | null;
  avgOD?: number | null;
  maxOD?: number | null;
  ooROD?: number | null;
  minIDClass?: string;
  avgIDClass?: string;
  maxIDClass?: string;
  ooRClass?: string;
  minODClass?: string;
  avgODClass?: string;
  maxODClass?: string;
  ooRODClass?: string;
}

export interface ReportHistoryTolerances {
  nominal_id?: number;
  nominal_od?: number;
  nominal_id_max?: string | number;
  nominal_id_min?: string | number;
  nominal_od_max?: string | number;
  nominal_od_min?: string | number;
  oor_id_tolerance?: string | number;
  oor_od_tolerance?: string | number;
  highlightType?: string;
}

export interface ReportHistoryReportData {
  pipe_id?: number;
  version?: number;
  pipeStats?: Record<string, number>;
  pipesList?: ReportHistoryPipeRow[];
  graphPipes?: ReportHistoryPipeRow[];
  tolerances?: ReportHistoryTolerances;
  hasInnerRadiusData?: boolean;
  hasOuterRadiusData?: boolean;
  listHasPipes?: boolean;
  projectNumber?: string;
  generatedAt?: string;
}

export interface ReportHistoryListItem {
  id: number;
  reportType: string;
  reportName: string;
  generatedAt: Date;
  generatedBy: string;
  status: string;
  projectId?: number;
  projectNumber: string;
  fileUrl?: string;
  fileRefs?: ReportFileRef[];
  pipeCount?: number;
  pipeIds?: string[];
  reportData?: any | null;
}

export interface ReportHistoryVisualizationPayload {
  id: number;
  reportType: string;
  reportName: string;
  projectId?: string | number;
  projectNumber: string;
  status: string;
}

const API_TO_REPORT_TYPE: Record<string, string> = {
  PIPE_DATA: 'PIPE_DATA',
  PIPE_COMPATIBILITY: 'PIPE_COMPATIBILITY',
};

function extractReportHistoryRows(body: unknown): Record<string, unknown>[] {
  if (!body || typeof body !== 'object') return [];
  const envelope = body as Record<string, unknown>;
  const status = Number(envelope.status);
  const ok = status === 200 || envelope.success === 1 || envelope.success === true;
  if (!ok) return [];

  const raw = envelope.data;
  if (Array.isArray(raw)) {
    return raw.filter((row): row is Record<string, unknown> => row != null && typeof row === 'object');
  }
  if (raw && typeof raw === 'object') {
    return [raw as Record<string, unknown>];
  }
  return [];
}

function normalizeReportHistoryRow(raw: Record<string, unknown>): ReportHistoryListItem | null {
  const id = Number(raw.id);
  if (!Number.isFinite(id) || id <= 0) return null;

  const reportType = String(raw.reportType ?? raw.report_type ?? 'PIPE_DATA');
  const normalizedType = API_TO_REPORT_TYPE[reportType] ?? reportType;
  const fileUrlRaw = raw.fileUrl ?? raw.file_url;
  const fileRefs = parseReportFileRefs(fileUrlRaw);
  const primaryFileUrl = pickReportFileUrlByType(fileUrlRaw, normalizedType);
  const primaryFileName = pickReportFileNameByType(fileUrlRaw, normalizedType);
  const reportDataRaw =
    raw.reportData && typeof raw.reportData === 'object'
      ? (raw.reportData as Record<string, unknown>)
      : null;

  return {
    id,
    reportType: normalizedType,
    reportName: String(
      raw.reportName ?? raw.report_name ?? primaryFileName ?? 'Pipe Data Report'
    ),
    generatedAt:
      raw.generatedAt || raw.generated_at || raw.createdAt || raw.created_at
        ? new Date(
            String(raw.generatedAt ?? raw.generated_at ?? raw.createdAt ?? raw.created_at)
          )
        : new Date(),
    generatedBy: String(raw.generatedBy ?? raw.generated_by ?? ''),
    status: String(raw.status ?? 'Completed'),
    projectId: raw.projectId != null ? Number(raw.projectId) : raw.project_id != null ? Number(raw.project_id) : undefined,
    projectNumber: String(raw.projectNumber ?? raw.project_number ?? ''),
    fileUrl: primaryFileUrl || undefined,
    fileRefs: fileRefs.length > 0 ? fileRefs : undefined,
    pipeCount: raw.pipeCount != null ? Number(raw.pipeCount) : raw.pipe_count != null ? Number(raw.pipe_count) : undefined,
    pipeIds: normalizePipeIdsForGroup(
      raw.pipeIds ??
        raw.pipe_ids ??
        raw.selectedPipeStatusIds ??
        raw.selected_pipe_status_ids ??
        reportDataRaw?.selectedPipeStatusIds ??
        reportDataRaw?.selected_pipe_status_ids
    ),
    reportData:
      reportDataRaw ? (reportDataRaw as ReportHistoryReportData) : null,
  };
}

export interface ReportHistoryListPagination {
  skip?: number;
  limit?: number;
}

const REPORT_HISTORY_DEFAULT_LIMIT = 10;
const REPORT_HISTORY_MAX_LIMIT = 100;

function normalizeReportHistoryPagination(
  pagination?: ReportHistoryListPagination
): { skip: number; limit: number } {
  const skip =
    typeof pagination?.skip === 'number' && Number.isFinite(pagination.skip)
      ? Math.max(0, Math.floor(pagination.skip))
      : 0;
  const limit =
    typeof pagination?.limit === 'number' && Number.isFinite(pagination.limit)
      ? Math.max(1, Math.min(REPORT_HISTORY_MAX_LIMIT, Math.floor(pagination.limit)))
      : REPORT_HISTORY_DEFAULT_LIMIT;
  return { skip, limit };
}

async function fetchReportHistoryList(
  projectNumber: string,
  projectId?: string | number,
  pipeId?: string | number,
  pagination?: ReportHistoryListPagination
): Promise<ReportHistoryListItem[]> {
  const { skip, limit } = normalizeReportHistoryPagination(pagination);
  const params = new URLSearchParams();
  if (projectNumber) params.set('projectNumber', projectNumber);
  if (projectId != null && projectId !== '') params.set('projectId', String(projectId));
  if (pipeId != null && pipeId !== '') params.set('pipeId', String(pipeId));
  params.set('skip', String(skip));
  params.set('limit', String(limit));

  const { data } = await pipefitterApi.get(`/reportgen/report-history?${params.toString()}`);
  return extractReportHistoryRows(data)
    .map((row) => normalizeReportHistoryRow(row))
    .filter((row): row is ReportHistoryListItem => row != null);
}

export async function listReportHistory(
  projectNumber: string,
  projectId?: string | number,
  reportType = 'PIPE_DATA',
  pipeId?: string | number,
  pagination?: ReportHistoryListPagination
): Promise<ReportHistoryListItem[]> {
  const rows = await listProjectReportHistory(projectNumber, projectId, pipeId, pagination);
  return rows.filter((row) => row.reportType === reportType);
}

export async function listProjectReportHistory(
  projectNumber: string,
  projectId?: string | number,
  pipeId?: string | number,
  pagination?: ReportHistoryListPagination
): Promise<ReportHistoryListItem[]> {
  const scoped = await fetchReportHistoryList(projectNumber, projectId, pipeId, pagination);
  if (scoped.length > 0 || pipeId == null || pipeId === '') {
    return scoped;
  }

  // Reports are often stored without material pipeId — retry at project scope.
  return fetchReportHistoryList(projectNumber, projectId, undefined, pagination);
}

export async function fetchReportHistoryVisualization(
  payload: ReportHistoryVisualizationPayload
): Promise<Record<string, unknown>> {
  const { data } = await pipefitterApi.post('/reportgen/report-history', payload);
  const ok = data?.status === 200 || data?.success === 1;
  if (!ok) {
    throw new Error(data?.message || 'Failed to load report visualization data');
  }

  const record = Array.isArray(data.data) ? data.data[0] : data.data;
  if (!record || typeof record !== 'object') {
    throw new Error('Report visualization response was empty');
  }
  return record as Record<string, unknown>;
}

function compatibilityArraysAreEmpty(data: Record<string, unknown>): boolean {
  const isAllZeros = (array: unknown) =>
    Array.isArray(array) && array.length > 0 && array.every((num) => Number(num) === 0);
  const omnifit = data.numberOfJointsPerHiloForGraph ?? data.numberOfJointsPerHilo ?? data.numberOfPipesPerHilo;
  const optifit =
    data.numberOfJointsPerHiloOptifitForGraph ??
    data.numberOfJointsPerHiloOptifit ??
    data.numberOfPipesPerHiloOptifit;
  const random =
    data.numberOfJointsPerHiloInRandomForGraph ??
    data.numberOfJointsPerHiloInRandom ??
    data.numberOfPipesPerHiloInRandom;
  return isAllZeros(omnifit) && isAllZeros(optifit) && isAllZeros(random);
}

/** Pull pipe compatibility chart fields from stored reportData (nested or flat). */
export function extractPipeCompatibilityFromReportData(
  reportData: unknown
): Record<string, unknown> | null {
  if (!reportData || typeof reportData !== 'object') return null;
  const rd = reportData as Record<string, unknown>;

  if (rd.pipeCompatibility && typeof rd.pipeCompatibility === 'object') {
    return rd.pipeCompatibility as Record<string, unknown>;
  }

  if (!compatibilityArraysAreEmpty(rd)) {
    return rd;
  }

  return null;
}

export function hasPipeCompatibilityVisualization(reportData: unknown): boolean {
  const compat = extractPipeCompatibilityFromReportData(reportData);
  return compat != null && !compatibilityArraysAreEmpty(compat);
}

/** True when object already looks like pipeCompatibility chart payload. */
export function hasCompatibilityChartData(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  return !compatibilityArraysAreEmpty(data as Record<string, unknown>);
}

export interface CompatibilityHistoryLookup {
  historyId?: number;
  docId?: number;
  pipeIds?: string[];
}

/** Find pipeCompatibility chart data across cached report-history rows. */
export function findCompatibilityInHistories(
  histories: ReportHistoryListItem[],
  entry: CompatibilityHistoryLookup
): Record<string, unknown> | null {
  const direct = histories.find(
    (row) => row.id === entry.historyId || row.id === entry.docId
  );
  const fromDirect = extractPipeCompatibilityFromReportData(direct?.reportData);
  if (fromDirect && hasCompatibilityChartData(fromDirect)) {
    return fromDirect;
  }

  const entryPipeIds = entry.pipeIds ?? [];
  for (const row of histories) {
    if (entryPipeIds.length > 0) {
      const rowPipeIds = row.pipeIds ?? [];
      if (rowPipeIds.length > 0 && !pipeIdSetsMatch(rowPipeIds, entryPipeIds)) {
        continue;
      }
    }
    const compat = extractPipeCompatibilityFromReportData(row.reportData);
    if (compat && hasCompatibilityChartData(compat)) {
      return compat;
    }
  }

  return null;
}

function pipeIdSetsMatch(left: string[], right: string[]): boolean {
  if (!left.length || !right.length) return false;
  return (
    [...left].map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(',') ===
    [...right].map(String).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(',')
  );
}

/** Pull pipe data visualization fields from stored reportData (nested pipeData or flat). */
export function extractPipeDataVisualizationFromReportData(
  reportData: unknown
): ReportHistoryReportData | null {
  if (!reportData || typeof reportData !== 'object') return null;
  const rd = reportData as Record<string, unknown>;
  const nestedPipeData =
    rd.pipeData && typeof rd.pipeData === 'object'
      ? (rd.pipeData as ReportHistoryReportData)
      : null;
  const candidate = nestedPipeData ?? (rd as ReportHistoryReportData);
  if ((candidate.pipesList?.length ?? 0) > 0 || (candidate.graphPipes?.length ?? 0) > 0) {
    return candidate;
  }
  return null;
}

export function extractPipeCompatibilityFromHistoryRecord(
  record: unknown
): Record<string, unknown> {
  if (!record || typeof record !== 'object') return {};
  const r = record as Record<string, unknown>;
  return (
    extractPipeCompatibilityFromReportData(r.reportData) ??
    extractPipeCompatibilityFromReportData(r) ??
    {}
  );
}

/** Map report-history visualization payload into pipe data report state fields. */
export function extractPipeDataFromReportHistory(record: Record<string, unknown>) {
  const reportData =
    record.reportData && typeof record.reportData === 'object'
      ? (record.reportData as ReportHistoryReportData)
      : null;

  const nested =
    reportData ||
    (record.visualizationData && typeof record.visualizationData === 'object'
      ? (record.visualizationData as ReportHistoryReportData)
      : (record as unknown as ReportHistoryReportData));

  const pipeDataNested =
    nested &&
    typeof nested === 'object' &&
    'pipeData' in nested &&
    nested.pipeData &&
    typeof nested.pipeData === 'object'
      ? (nested.pipeData as ReportHistoryReportData)
      : null;

  const visualization = pipeDataNested ?? nested;
  const pipesList = Array.isArray(visualization.pipesList) ? visualization.pipesList : [];
  const graphPipes = Array.isArray(visualization.graphPipes) ? visualization.graphPipes : pipesList;
  const pipeStats = visualization.pipeStats || {};
  const pipeCompatibility =
    extractPipeCompatibilityFromReportData(reportData ?? nested) ?? {};

  return {
    reportData: extractPipeDataVisualizationFromReportData(reportData ?? nested) ?? reportData,
    pipesListFiltered: pipesList,
    pipeScan: [],
    pipeStatistics: pipeStats,
    pipeCompatibility,
    pipeDataGraph: graphPipes,
  };
}

export type VisualizationReportType = 'pipeData' | 'pipeCompatibility';

export interface VisualizationReportHistoryRow {
  id: string;
  serverId?: number;
  reportType: VisualizationReportType;
  reportName: string;
  generatedAt: Date;
  generatedBy: string;
  pipeCount: number;
  pipeIds: { pipeEndId: string; id: string | number }[];
  status: 'Started' | 'Processing' | 'Completed' | 'Failed';
  fileUrl?: string;
  fileUrls?: ReportFileRef[];
}

const UI_REPORT_TYPE_FROM_API: Record<string, VisualizationReportType> = {
  PIPE_DATA: 'pipeData',
  PIPE_COMPATIBILITY: 'pipeCompatibility',
};

function normalizeModalPipeIds(
  pipeIds: unknown
): { pipeEndId: string; id: string | number }[] {
  if (!Array.isArray(pipeIds)) return [];
  return pipeIds.map((p, index) => {
    if (typeof p === 'string') return { pipeEndId: p, id: index + 1 };
    if (p && typeof p === 'object') {
      const row = p as { pipeEndId?: string; pipeID?: string; id?: string | number };
      const pipeEndId = row.pipeEndId ?? row.pipeID ?? String(row.id ?? index + 1);
      return { pipeEndId: String(pipeEndId), id: row.id ?? index + 1 };
    }
    return { pipeEndId: String(p), id: index + 1 };
  });
}

function mapHistoryRowToVisualization(row: ReportHistoryListItem): VisualizationReportHistoryRow {
  const fileRefs = row.fileRefs ?? [];
  return {
    id: String(row.id),
    serverId: row.id,
    reportType: UI_REPORT_TYPE_FROM_API[row.reportType] ?? 'pipeData',
    reportName: row.reportName,
    generatedAt: row.generatedAt,
    generatedBy: row.generatedBy,
    pipeCount: row.pipeCount ?? row.pipeIds?.length ?? 0,
    pipeIds: normalizeModalPipeIds(row.pipeIds),
    status: (row.status as VisualizationReportHistoryRow['status']) ?? 'Completed',
    fileUrl: row.fileUrl,
    fileUrls: fileRefs.length > 0 ? fileRefs : undefined,
  };
}

function mapVersionRowToVisualization(
  row: ReportVersionRow,
  reportType: VisualizationReportType
): VisualizationReportHistoryRow {
  return {
    id: `version-${row.docId}`,
    serverId: row.docId,
    reportType,
    reportName: row.fileName,
    generatedAt: row.uploadDate ? new Date(row.uploadDate) : new Date(),
    generatedBy: row.uploadedBy ?? '',
    pipeCount: row.pipeCount ?? row.pipeIds?.length ?? 0,
    pipeIds: normalizeModalPipeIds(row.pipeIds),
    status: 'Completed',
    fileUrl: row.fileUrl,
    fileUrls: row.fileUrl
      ? [{ docRef: row.docRef, fileUrl: row.fileUrl, fileName: row.fileName }]
      : undefined,
  };
}

function ingestVersionRows(
  envelope: Awaited<ReturnType<typeof getReportVersions>>,
  reportType: VisualizationReportType,
  knownServerIds: Set<number>
): VisualizationReportHistoryRow[] {
  if (isReportVersionsFailure(envelope)) return [];
  const rawList = envelope.data?.versions;
  if (!Array.isArray(rawList)) return [];
  return rawList
    .map(normalizeReportVersionRow)
    .filter((row) => row.docId > 0 || Boolean(row.fileUrl))
    .filter((row) => !knownServerIds.has(row.docId))
    .map((row) => mapVersionRowToVisualization(row, reportType));
}

/** Report history for visualization modal — scoped by project and material pipe size. */
export async function loadVisualizationReportHistory(params: {
  projectNumber: string;
  projectId?: string | number;
  pipeId?: string | number;
}): Promise<VisualizationReportHistoryRow[]> {
  const { projectNumber, projectId, pipeId } = params;
  const [historyRows, pipeDataEnvelope, compatibilityEnvelope] = await Promise.all([
    listProjectReportHistory(projectNumber, projectId, pipeId, { skip: 0, limit: 100 }),
    getReportVersions({ projectNumber, reportType: PIPEFITTER_REPORT_TYPES.PIPE_DATA }),
    getReportVersions({ projectNumber, reportType: PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY }),
  ]);

  const history = historyRows.map(mapHistoryRowToVisualization);
  const knownServerIds = new Set(
    history.map((row) => row.serverId).filter((id): id is number => typeof id === 'number' && id > 0)
  );

  const versionRows = [
    ...ingestVersionRows(pipeDataEnvelope, 'pipeData', knownServerIds),
    ...ingestVersionRows(compatibilityEnvelope, 'pipeCompatibility', knownServerIds),
  ];

  return [...history, ...versionRows].sort(
    (a, b) => b.generatedAt.getTime() - a.generatedAt.getTime()
  );
}
